<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import { ButtonType, CustomButton } from '@/components/common/';
import type { CaseModel } from '@/models';
import { useStudyStore } from '@/stores/study';
import { useCaseNoteStore } from '@/stores/caseNote';
import type { TimepointOption } from '@/models/caseNote';
import { parseTimepointToMinutes } from '@/utils';
import CaseNoteDialog from './CaseNoteDialog.vue';
import CaseNotesPanel from './CaseNotesPanel.vue';

const props = defineProps({
  caseModel: {
    type: Object as PropType<CaseModel>,
    required: true
  },
  showText: {
    type: Boolean,
    default: false
  },
  onSidebar: {
    type: Boolean,
    default: false
  }
});

const showNoteDialog = ref(false);
const showNotesPanel = ref(false);
const studyStore = useStudyStore();
const caseNoteStore = useCaseNoteStore();
const localCaseNoteCount = ref(props.caseModel.caseNoteCount ?? 0);

watch(
  () => props.caseModel.caseNoteCount,
  newCount => {
    localCaseNoteCount.value = newCount ?? 0;
  }
);

const pumpTimepointOptions = computed<TimepointOption[]>(() => {
  if (studyStore.selectedStudy?.flags?.customDataDictionary !== 'PUMP') {
    return [];
  }

  const dataObjectKey = `${studyStore.selectedStudy?.flags?.customDataDictionary}_DATA`;
  //@ts-expect-error: dynamic data for PUMP
  const dataDictionary = studyStore.selectedStudy?.flags?.[dataObjectKey] as Record<string, string> | undefined;
  if (!dataDictionary) {
    return [];
  }

  const firstMapping = Object.entries(dataDictionary)[0];
  if (!firstMapping) {
    return [];
  }

  const [formIdentifier, fieldIdentifier] = firstMapping;
  const entries = props.caseModel.entries?.[formIdentifier] ?? [];
  const values = new Set<string>();

  for (const entry of entries) {
    const rawValue = entry.items?.[fieldIdentifier];
    if (rawValue === undefined || rawValue === null) {
      continue;
    }

    const timepoint = `${rawValue}`.trim();
    if (timepoint) {
      values.add(timepoint);
    }
  }

  return Array.from(values)
    .sort((a, b) => {
      const aMinutes = parseTimepointToMinutes(a);
      const bMinutes = parseTimepointToMinutes(b);

      if (aMinutes !== bMinutes) {
        return aMinutes - bMinutes;
      }

      return a.localeCompare(b);
    })
    .map(value => ({
      label: value,
      value
    }));
});

const emit = defineEmits<{
  onPinChange: [pinned: boolean];
}>();

const togglePanel = (event: MouseEvent) => {
  event.stopPropagation();

  const redirectedToPinnedPanel = caseNoteStore.requestPinnedPanelCase(
    props.caseModel.identifier,
    props.caseModel.label
  );
  if (redirectedToPinnedPanel) {
    return;
  }

  showNotesPanel.value = true;
};

const handleNoteSaved = async () => {
  showNoteDialog.value = false;
  if (showNotesPanel.value) {
    await caseNoteStore.getNotes(props.caseModel.identifier);
    localCaseNoteCount.value = caseNoteStore.notes.length;
    return;
  }

  localCaseNoteCount.value += 1;
};
</script>

<template>
  <div class="note-button-root"
    :class="{ 'has-notes': localCaseNoteCount > 0, 'on-sidebar': onSidebar }"
    @click.stop>
    <div class="note-btn-wrapper">
      <CustomButton :icon="{ icon: 'note-sticky', style: 'fal' }"
        :type="ButtonType.ICON_ONLY"
        tooltip="Case notes"
        v-if="!showText" 
        @onClick="togglePanel"/>

      <CustomButton :icon="{ icon: 'note-sticky', style: 'far' }"
        :type="ButtonType.OUTLINED"
        text="Notes"
        v-if="showText"
        @onClick="togglePanel"/>
      <span v-if="localCaseNoteCount > 0" :class="showText || onSidebar ? 'note-badge-has-text' : 'note-badge'">
        {{ localCaseNoteCount > 99 ? '99+' : localCaseNoteCount }}
      </span>
    </div>

    <CaseNoteDialog :visible="showNoteDialog"
      :case-identifier="caseModel.identifier"
      :timepoint-options="pumpTimepointOptions"
      @onClose="showNoteDialog = false"
      @onSaved="handleNoteSaved"/>

    <CaseNotesPanel :visible="showNotesPanel"
      :case-identifier="caseModel.identifier"
      :case-label="caseModel.label"
      :timepoint-options="pumpTimepointOptions"
      :show-pin-button="true"
      @onClose="showNotesPanel = false"
      @onAddNote="showNoteDialog = true"
      @onPinChange="emit('onPinChange', $event)"/>
  </div>
</template>

<style scoped lang="scss">
@import '../../../assets/_variables.module.scss';

.note-button-root {
  display: inline-flex;
  align-items: center;
}

.note-btn-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.note-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 12px;
  height: 12px;
  padding: 0 2px;
  border-radius: 8px;
  background: var(--p-primary-color, #0066ff);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
  text-align: center;
  pointer-events: none;
  z-index: 1;
}

.note-badge-has-text {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 2px;
  border-radius: 8px;
  background: var(--p-primary-color, #0066ff);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  pointer-events: none;
  z-index: 1;
}

:deep(.icon-only) {
  padding: unset;
  min-width: unset;
}
.on-sidebar {
  :deep(.icon) {
    width: 24px;
    height: 24px;
  }
}
</style>
